import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { AppSettings } from 'src/app/Constant/AppSettings';
import { FileUpload } from 'src/app/models/FileUpload';
import { ClientcommunicationService } from 'src/app/services/clientcommunication/clientcommunication.service';
import { ClientDataService } from 'src/app/services/client-data/client-data.service';
declare var CKEDITOR: any;

@Component({
  selector: 'app-broadcast-message',
  templateUrl: './broadcast-message.component.html',
  styleUrls: ['./broadcast-message.component.css'],
  animations: [
    trigger('animation', [
      state('visible', style({
        transform: 'translateX(0)',
        opacity: 1
      })),
      transition('void => *', [
        style({ transform: 'translateX(50%)', opacity: 0 }),
        animate('300ms ease-out')
      ]),
      transition('* => void', [
        animate(('250ms ease-in'), style({
          height: 0,
          opacity: 0,
          transform: 'translateX(50%)'
        }))
      ])
    ])
  ]
})
export class BroadcastMessageComponent implements OnInit, OnDestroy {
  broadcastList: any[] = [];
  clients: any[] = [];
  orgClients: any[] = [];
  businessTypes: any[] = [];
  selectedBusinessTypes: string[] = [];
  filterToggle: boolean = false;
  broadcastLoading: boolean = false;
  businessTypeSelectAll: boolean = false;
  isLoading: boolean = false;
  disabled: boolean = false
  searchText = '';
  msgText: string = '';
  selectAll: boolean = false;

  // Rich text composer
  displayToolbar: boolean = true;
  editorInitialized: boolean = false;

  // Attachments
  selectedFiles: any[] = [];
  finalSelectedFiles?: FileList;
  currentFileUpload?: FileUpload;
  downloadUrlList: any[] = [];
  percentage = 0;

  // Broadcast Logs
  // Lands on the log list by default; "New Broadcast Message" switches to the composer.
  showLogsPanel: boolean = true;
  broadcastLogs: any[] = [];
  selectedLog: any = null;
  showLogDetail: boolean = false;

  constructor(
    private clientcommunicationService: ClientcommunicationService,
    private clientDataService: ClientDataService,
    private messageService: MessageService,
    private sanitizer: DomSanitizer,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.listClient();
    this.listBusinessTypes();
    this.loadBroadcastLogs();
  }

  private ckEditorPoll: any;

  /** The composer (and its #broadcastEditor textarea) only exists in the DOM while
   * !showLogsPanel — unlike a one-shot init, a standing poll picks it up both on first load
   * (also covering the case where a hard refresh lands here before the deferred
   * assets/js/ckeditor.js <script> in index.html has finished loading — referencing the bare
   * CKEDITOR global before it's defined throws a ReferenceError) and every time the user re-enters
   * compose mode after the editor was destroyed on a previous exit. */
  ngAfterViewInit(): void {
    this.ckEditorPoll = setInterval(() => this.tryInitCKEditor(), 300);
  }

  private tryInitCKEditor(): void {
    if (typeof CKEDITOR === 'undefined' || this.showLogsPanel) return;
    if (CKEDITOR.instances && CKEDITOR.instances['broadcastEditor']) return;
    if (!document.getElementById('broadcastEditor')) return;
    this.initCKEditor();
  }

  /** Destroys the live editor instance (if any) so the next tryInitCKEditor() tick creates a
   * fresh one against the new textarea Angular renders next time compose mode opens. */
  private destroyCKEditor(): void {
    if (typeof CKEDITOR !== 'undefined' && CKEDITOR.instances && CKEDITOR.instances['broadcastEditor']) {
      CKEDITOR.instances['broadcastEditor'].destroy(true);
    }
    this.editorInitialized = false;
  }

  ngOnDestroy(): void {
    clearInterval(this.ckEditorPoll);
    this.destroyCKEditor();
  }

  /** Business types narrowed by the search box while the filter panel is open. */
  get filteredBusinessTypes(): any[] {
    if (!this.searchText) {
      return this.businessTypes;
    }
    const searchText = this.searchText.toLocaleLowerCase();
    return this.businessTypes.filter(type => (type.name || '').toLocaleLowerCase().includes(searchText));
  }

  listBusinessTypes() {
    this.clientDataService.getBusinessType().subscribe(
      (response) => {
        var json = JSON.parse(JSON.stringify(response));
        if (json.response.status == 'SUCCESS') {
          this.businessTypes = json.data;
        }
      },
      (error) => {
        console.log(error);
      }
    );
  }

  /** Toggles the sidebar between the client picker and the business-type filter panel. */
  handleFilter() {
    this.broadcastLoading = true;
    setTimeout(() => {
      this.broadcastLoading = false;
      this.searchText = '';
      this.filterToggle = !this.filterToggle;
    }, 250);
  }

  cancelFilter() {
    this.businessTypeSelectAll = false;
    this.clients = this.orgClients;
    this.selectedBusinessTypes = [];
    this.filterToggle = !this.filterToggle;
  }

  filterClientByBusinessType() {
    if (this.selectedBusinessTypes.length > 0) {
      this.clients = this.orgClients.filter(client =>
        this.selectedBusinessTypes.includes('' + client.businessType?.id)
      );
    } else {
      this.clients = this.orgClients;
    }
    this.filterToggle = !this.filterToggle;
  }

  businessTypeSelectOrDeselectAll(event: any) {
    if (event.checked) {
      this.selectedBusinessTypes = this.businessTypes.map(type => '' + type.id);
    } else {
      this.selectedBusinessTypes = [];
    }
  }

  prepareMessageBeforeSending() {
    this.msgText = CKEDITOR.instances.broadcastEditor.getData();

    if (this.broadcastList.length == 0 || this.broadcastList == undefined) {
      this.showErrorToastMsg('Please select client.')
      return;
    }
    if ((this.msgText.toString().trim() == '' || this.msgText == null) && this.selectedFiles.length == 0) {
      this.showErrorToastMsg('Please type message or attach files before sending.')
      return;
    }

    if (this.selectedFiles.length != 0) {
      this.saveFilesToStorage();
    } else {
      this.sendBulkMessage();
    }
  }

  /** Sends the composed message + attachments to every selected business in one call. */
  sendBulkMessage() {
    this.showProgressBar();
    const businessIds = this.broadcastList.map((b: any) => b.businessId);
    this.clientcommunicationService.sendSupportBroadcastRest(businessIds, this.msgText, this.downloadUrlList).subscribe(
      () => {
        this.loadBroadcastLogs();
        this.showSuccessMessage('SUCCESS', 'Broadcast sent successfully.')
        this.clearComposer();
        this.clearFiles();
        this.backToLogs();
        this.hideProgressBar()
      },
      (error) => {
        console.log(error);
        this.showErrorToastMsg(AppSettings.ERROR_MSG)
        this.hideProgressBar()
      }
    );
  }

  /** Resets the composer and recipient picker back to a clean state after a successful send. */
  clearComposer() {
    this.msgText = '';
    CKEDITOR.instances.broadcastEditor.setData('');
    this.broadcastList = [];
    this.selectAll = false;
    this.searchText = '';
    this.selectedBusinessTypes = [];
    this.businessTypeSelectAll = false;
    this.filterToggle = false;
    this.clients = this.orgClients;
  }

  /**
   * Fetches every business for the broadcast picker, not just one page — pages through
   * getBusinessList() (the already-correctly-paginated endpoint used by Business Management)
   * instead of relying on getAllBusinessList()'s single request, which silently caps at whatever
   * the backend's default page size is. Mirrors the fix already applied in
   * clientcommunication.component.ts's listClient().
   */
  listClient() {
    this.showProgressBar();
    this.clientDataService.getBusinessList(1, '', '', null).subscribe(
      (response) => {
        var json = JSON.parse(JSON.stringify(response));
        if (json.response.status != 'SUCCESS') {
          this.showErrorToastMsg(json.response.displayMessage)
          this.hideProgressBar();
          return;
        }

        const firstPage: any[] = json.data || [];
        const totalPages: number = json.additionalData?.totalPages || 1;

        if (totalPages <= 1) {
          this.clients = firstPage;
          this.orgClients = firstPage;
          this.hideProgressBar();
          return;
        }

        const remainingPageRequests = [];
        for (let page = 2; page <= totalPages; page++) {
          remainingPageRequests.push(this.clientDataService.getBusinessList(page, '', '', null));
        }

        forkJoin(remainingPageRequests).subscribe(
          (responses: any[]) => {
            let allClients = [...firstPage];
            responses.forEach((r: any) => {
              const pageJson = JSON.parse(JSON.stringify(r));
              if (pageJson.response.status == 'SUCCESS') {
                allClients = allClients.concat(pageJson.data || []);
              }
            });
            this.clients = allClients;
            this.orgClients = allClients;
            this.hideProgressBar();
          },
          (error) => {
            console.log(error);
            this.clients = firstPage;
            this.orgClients = firstPage;
            this.showErrorToastMsg(AppSettings.ERROR_MSG)
            this.hideProgressBar();
          }
        );
      },
      (error) => {
        console.log(error);
        this.showErrorToastMsg(AppSettings.ERROR_MSG)
        this.hideProgressBar();
      }
    );
  }

  addToBroadcastList(client: any) {
    const visitorAlreadyInArray = this.broadcastList.some(obj => this.objectsAreEqual(obj, client))
    if (!visitorAlreadyInArray) {
      this.broadcastList.push(client);
    }
  }

  removeToBroadcastList(client: any) {
    const index = this.broadcastList.indexOf(client);
    if (index > -1) {
      this.broadcastList.splice(index, 1);
    }
  }

  objectsAreEqual(obj1: any, obj2: any) {
    return obj1.id === obj2.id
  }

  selectOrDeselectAll(event: any) {
    if (event.checked) {
      this.broadcastList = [];
      this.clients.forEach(element => {
        this.broadcastList.push(element);
      });
    } else {
      this.broadcastList = [];
    }
  }

  showErrorMessage(msg: string) {
    this.messageService.add({
      key: 'errMsg',
      severity: 'error',
      detail: msg,
    });
  }

  showErrorToastMsg(msg: string) {
    this.messageService.clear('br');
    this.messageService.add({
      key: 'br',
      severity: 'error',
      detail: msg,
    });
  }

  showSuccessMessage(status: string, msg: string) {
    this.messageService.clear('br');
    this.messageService.add({
      key: 'br',
      severity: 'success',
      summary: status,
      detail: msg,
    });
  }

  hideProgressBar() {
    this.isLoading = false;
    this.disabled = false;
  }

  showProgressBar() {
    this.messageService.clear('errMsg');
    this.isLoading = true;
    this.disabled = true;
  }

  // ---- Rich text composer (CKEditor) ----

  initCKEditor() {
    if (this.editorInitialized) return;
    if (CKEDITOR.instances && CKEDITOR.instances['broadcastEditor']) {
      this.editorInitialized = true;
      return;
    }

    const checkElement = document.getElementById('broadcastEditor');
    if (!checkElement) return;

    this.editorInitialized = true;

    // Run outside Angular zone so CKEditor's internal timers don't trigger change detection
    this.ngZone.runOutsideAngular(() => {
      CKEDITOR.replace('broadcastEditor', {
        toolbar: [
          { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', '-'] },
          { name: 'styles', items: ['Styles', 'Format', 'Font', 'FontSize'] },
          { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
          { name: 'insert', items: ['Smiley'] },
          { name: 'links', items: ['Link', 'Unlink'] },
          { name: 'colors', items: ['TextColor', 'BGColor'] },
          { name: 'tools', items: ['Maximize'] },
        ],
        height: '70px',
        removePlugins: 'elementspath,exportpdf',
        resize_enabled: false,
        stylesSet: [
          { name: 'Italic Title', element: 'h2', styles: { 'font-style': 'italic' } },
          { name: 'Subtitle', element: 'h3', styles: { 'color': '#aaa', 'font-style': 'italic' } },
          { name: 'Special Container', element: 'div', styles: { 'padding': '5px 10px', 'background': '#eee', 'border': '1px solid #ccc' } },
          { name: 'Bold', element: 'strong' },
          { name: 'Italic', element: 'em' },
          { name: 'Underline', element: 'u' },
          { name: 'Strike', element: 'strike' },
          { name: 'Big', element: 'big' },
          { name: 'Small', element: 'small' },
          { name: 'Sample Text', element: 'samp' }
        ],
        format_tags: 'p;h1;h2;h3;h4;h5;h6',
      });

      // Hide the toolbar by default
      let checkId = setInterval(() => {
        var toolbar = this.getToolbarElement();
        if (toolbar != null) {
          clearInterval(checkId);
          toolbar.style.display = 'none';
        }
      }, 100);
    });
  }

  /** Scoped to this specific editor instance's own container — the "Chat with Support" tab
   * (chat-area.component.ts) keeps its own CKEditor instance alive in the DOM in the background
   * (PrimeNG p-tabView doesn't destroy inactive tab content), so a page-wide
   * document.getElementsByClassName('cke_reset_all')[0] lookup can silently grab that other
   * instance's toolbar instead of this one's. */
  private getToolbarElement(): HTMLElement | null {
    const editor = CKEDITOR.instances && CKEDITOR.instances['broadcastEditor'];
    if (!editor || !editor.container) return null;
    return editor.container.$.querySelector('.cke_top');
  }

  showToolBar() {
    const toolbar = this.getToolbarElement();
    if (!toolbar) return;
    toolbar.style.display = this.displayToolbar ? 'block' : 'none';
    this.displayToolbar = !this.displayToolbar;
  }

  // ---- Attachments ----

  selectFile(event: any): void {
    const newFiles = event.target.files;
    const existingFiles = this.finalSelectedFiles ? Array.from(this.finalSelectedFiles) : [];
    const allFiles = [...existingFiles, ...Array.from(newFiles)];

    const dataTransfer = new DataTransfer();
    allFiles.forEach(file => dataTransfer.items.add(file as File));
    this.finalSelectedFiles = dataTransfer.files;

    for (let index = 0; index < newFiles.length; index++) {
      const file = newFiles[index];
      var size = file.size;
      var name = file.name;
      var extension = name.substring(name.lastIndexOf('.') + 1);
      if (size > 50000000) {
        let finalName = name;
        if (name.length > 20) {
          finalName = name.substring(0, 20) + '...' + extension;
        }
        this.showErrorMessage(finalName + ' File should not be more than 50 MB!')
      }
      if (file.type == 'image/jpeg' || file.type == 'image/png') {
        const reader = new FileReader();
        reader.onload = () => {
          file.src = reader.result as string;
          this.selectedFiles.push(file);
        }
        reader.readAsDataURL(file)
      } else {
        this.selectedFiles.push(file);
      }
    }
    event.target.value = '';
    if (this.checkLargeFileExist(this.selectedFiles)) {
      this.disabled = true
    } else {
      this.disabled = false
    }
  }

  upload(i: number): void {
    this.showProgressBar()
    if (this.finalSelectedFiles) {
      const file: File | null = this.finalSelectedFiles.item(i);
      if (file) {
        this.currentFileUpload = new FileUpload(file);
        this.uploadToStorage(this.currentFileUpload);
      }
    }
  }

  uploadToStorage(fileUpload: FileUpload): void {
    this.clientcommunicationService.pushFileToStorage(fileUpload, 'SYSTEM_ADMIN_BROADCAST').subscribe({
      next: (event: HttpEvent<any>) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.percentage = Math.round((100 * event.loaded) / event.total);
        } else if (event.type === HttpEventType.Response) {
          const attachments: { name: string, url: string }[] = event.body?.data || [];
          attachments.forEach(a => this.downloadUrlList.push({ url: a.url, name: a.name }));
          if (this.selectedFiles.length == this.downloadUrlList.length) {
            this.sendBulkMessage();
          }
        }
      },
      error: (error) => {
        console.log(error);
        this.hideProgressBar()
        this.showErrorToastMsg(AppSettings.ERROR_MSG)
      }
    });
  }

  saveFilesToStorage() {
    for (let index = 0; index < this.selectedFiles.length; index++) {
      this.upload(index)
    }
  }

  clearFiles() {
    this.selectedFiles = [];
    this.finalSelectedFiles = undefined;
    this.downloadUrlList = [];
    this.messageService.clear('errMsg');
  }

  removeToFileList(file: any) {
    let index = this.selectedFiles.indexOf(file)
    if (index != -1) {
      this.selectedFiles.splice(index, 1)
    }
    if (this.selectedFiles.length == 0) {
      this.clearFiles()
    }
    if (this.checkLargeFileExist(this.selectedFiles)) {
      this.disabled = true
    } else {
      this.messageService.clear('errMsg');
      this.disabled = false
    }
  }

  checkLargeFileExist(array: any) {
    return array.some((file: any) => {
      return file.size > 50000000;
    });
  }

  // ---- Broadcast Logs ----

  startNewBroadcast() {
    this.showLogsPanel = false;
  }

  backToLogs() {
    this.destroyCKEditor();
    this.showLogsPanel = true;
  }

  loadBroadcastLogs() {
    this.clientcommunicationService.getAdminBroadcastLogsRest().subscribe(
      (response: any) => {
        var json = JSON.parse(JSON.stringify(response));
        if (json.response.status == 'SUCCESS') {
          this.broadcastLogs = json.data;
        } else {
          this.showErrorMessage(json.response.displayMessage);
        }
      },
      (error: any) => {
        console.log(error);
      }
    );
  }

  getTrimmedMessage(msg: string): string {
    const div = document.createElement('div');
    div.innerHTML = msg;
    const text = div.textContent || div.innerText || '';
    return text.length > 50 ? text.substring(0, 50) + '...' : text;
  }

  showLogDetails(log: any) {
    this.selectedLog = {
      ...log,
      sanitizedMessage: this.sanitizer.bypassSecurityTrustHtml(log.message)
    };
    this.showLogDetail = true;
  }

  closeLogDetail() {
    this.showLogDetail = false;
    this.selectedLog = null;
  }
}
